import { get, first } from 'lodash'
import type { Schema } from '@wix/wix-data-schema-types'
import type {
  AppApi,
  EDITOR_TYPE,
  PagesToAdd,
  PresetDefinition,
} from '@wix/wix-data-client-common'
import {
  isDbDriverCollection,
  PAGE_TYPES,
  getPageName as generateNewPageName,
} from '@wix/wix-data-client-common'
import type { CreatePreset } from './new-page/createOrConnectDynamicPage'
import { createOrConnectDynamicPage } from './new-page/createOrConnectDynamicPage'
import type { DataFixer } from './new-page/applyDataFixers'
import { applyDataFixers } from './new-page/applyDataFixers'
import addDPLinkToSiteMenu from '../addDPLinkToSiteMenu'
import { generateRouteDefinition } from './new-page/generateRouteDefinition'
import { isDynamicRoute } from '../isDynamicRoute'
import { prepareDataForPages } from './prepareDataForPages'
import { DATA_BINDING } from '@wix/app-definition-ids'
import { shouldUseSeoV2RouteConfig } from '../useSeoV2Infra'
import type { CreateOrConnectDPState } from '../../../types'
import type { EditorSDK, Logger } from '@wix/wix-data-client-common-standalone'
import type Experiments from '@wix/wix-experiments'
import type { HttpClient } from '@wix/http-client'
import { getCollectionFields } from '../utils/getCollectionFields'

const getPageLinkFieldKey = ({
  state,
  pageKey,
}: {
  state: CreateOrConnectDPState
  pageKey: string
}) => get(state.pageDefinitions[pageKey], 'pageLinkFieldKey')

const isFirst = (index: number) => index === 0

export const getPagesStepsCreator =
  ({
    appApi,
    editorType,
    editorSdkProxy,
    experimentsManager,
    logger,
    httpClient,
  }: {
    appApi: AppApi
    editorType: EDITOR_TYPE
    editorSdkProxy: EditorSDK
    experimentsManager: Experiments
    logger: Logger
    httpClient: HttpClient
  }) =>
  ({
    orderedPages,
    collectionId,
    collectionName,
    pageTitleBase = collectionName,
    preset,
    isFromExistingCollection,
    isIndexable,
    schema,
  }: {
    orderedPages: PagesToAdd
    collectionId: string
    collectionName: string
    pageTitleBase?: string
    preset: PresetDefinition
    isFromExistingCollection: boolean
    isIndexable: boolean
    schema: Schema
  }): {
    action: ({ state }: { state: CreateOrConnectDPState }) => Promise<void>
  }[] =>
    orderedPages.map(({ key, staticPageRef, bindings }, stepIndex) => {
      const router = first(preset.routers)!
      const routerConfig = router.config

      const pageType =
        key === 'indexPage' ? PAGE_TYPES.COLLECTION : PAGE_TYPES.ITEM

      return {
        action: async ({ state }) => {
          const { collectionFields, formattedFields: fields } =
            getCollectionFields({
              schema,
              isFromExistingCollection,
            })

          const isEditPage = preset.id === 'edit'
          const isCategoryPage = preset.id === 'category'
          if (isFirst(stepIndex)) {
            const { routerPrefix, managingAppDefId, pageLinks } =
              await prepareDataForPages({
                appApi,
                editorSdkProxy,
                collectionId,
                collectionName,
                pagesToAdd: orderedPages,
                collectionFields,
                displayField: schema.displayField,
                isTPACollection: isDbDriverCollection(schema),
                pageType,
                isEditPage,
              })

            state.routerPrefix = routerPrefix
            state.managingAppDefId = managingAppDefId
            state.pageDefinitions =
              pageLinks as CreateOrConnectDPState['pageDefinitions']
          }

          const { pattern, url } = state.pageDefinitions[key]

          const itemPageLinkFieldKey = getPageLinkFieldKey({
            state,
            pageKey: 'itemPage',
          })

          const indexPageLinkFieldKey = getPageLinkFieldKey({
            state,
            pageKey: 'indexPage',
          })

          const page = preset.pages.find(p => p.type === key)!

          let result: DataFixer | null = null

          if (!staticPageRef) {
            result = await applyDataFixers({
              collectionId,
              page,
              pageType,
              presetId: preset.id,
              editorType,
              fields,
              displayFieldKey: schema.displayField!,
              itemPageLinkFieldKey,
              indexPageLinkFieldKey,
              editorSdkProxy,
              pattern,
              logger,
              isFromExistingCollection,
            })
          }

          const createPreset = result?.createPreset as CreatePreset
          const installedAppIds = result?.installedAppIds ?? new Map()

          state.installedAppIds = new Map([
            ...state.installedAppIds,
            ...installedAppIds,
          ])

          const convertedPage = orderedPages.find(page => page.staticPageRef)
          const useSeoV2RouteConfig = await shouldUseSeoV2RouteConfig({
            editorSdkProxy,
            experimentsManager,
          })

          const routeDefinition = generateRouteDefinition({
            schema,
            editorType,
            collectionId,
            routerConfig,
            routerPages: router.pages,
            pageId: page.id,
            route: pattern,
            isIndexable,
            requiresAdjustingToSchema: Boolean(
              isFromExistingCollection || convertedPage,
            ),
            seoV2: useSeoV2RouteConfig,
          })

          const pageTitle = generateNewPageName({
            collectionName: pageTitleBase,
            pattern,
            isEditPage,
            isCategoryPage,
          })

          const { pageRef } = await createOrConnectDynamicPage({
            managingAppDefId: state.managingAppDefId,
            editorSdkProxy,
            appApi,
            pageType,
            prefix: state.routerPrefix!,
            pattern,
            createPreset,
            routeDefinition,
            pageRef: staticPageRef,
            pageTitle,
            isEditPage,
            collectionName,
            editorType,
            httpClient,
            logger,
            presetId: preset.id,
          })

          const isAutoBound = Boolean(bindings?.length) || !staticPageRef

          state.pages.push({
            key,
            pageType,
            pageRef,
            wasStatic: Boolean(staticPageRef),
            isAutoBound,
            pageUrl: url,
            pageTitle,
          })

          const isStaticRoute = !isDynamicRoute({ route: pattern })
          const isRegularDynamicPage = state.managingAppDefId === DATA_BINDING

          const needsLink = isStaticRoute && isRegularDynamicPage
          if (needsLink) {
            await addDPLinkToSiteMenu({
              editorSdkProxy,
              pageRef,
              innerRoute: pattern,
              label: collectionName,
            })
          }
        },
      }
    })
